import { IconExternalLink, IconTrash } from '@tabler/icons-react';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown, Sheet } from '@/components/common';
import { useCombobox } from '@/hooks';
import { navigate } from '@/lib/router';
import type { Booking, Patient } from '@/types/api';
import { atMinute, minutesOfDay, SLOT_MIN } from '@/utils/calendar';
import type { Doctor } from '@/utils/doctors';
import { formatDate } from '../chart/components';
import { patientsApi } from '../patients/api';
import type { BookingInput } from './api';

const DURATIONS = [30, 60, 90, 120, 150, 180, 210, 240];
const SUGGESTIONS = 6;

type Props = {
  /** Editing this booking, or a new one starting at `start`. */
  booking: Booking | null;
  start: Date;
  doctors: Doctor[];
  defaultDoctorId: string | null;
  onClose: () => void;
  onSave: (input: BookingInput) => Promise<void>;
  onCancelBooking?: () => Promise<void>;
};

const clock = (minute: number) =>
  `${String(Math.floor(minute / 60)).padStart(2, '0')}:${String(minute % 60).padStart(2, '0')}`;

/** Patients whose name or phone matches what was typed (free text is fine too). */
function useSuggestions(query: string, enabled: boolean): Patient[] {
  const [found, setFound] = useState<Patient[]>([]);
  useEffect(() => {
    const q = query.trim();
    if (!(enabled && q.length >= 2)) {
      setFound([]);
      return;
    }
    let live = true;
    const timer = setTimeout(() => {
      patientsApi
        .list(q, SUGGESTIONS)
        .then((list) => live && setFound(list))
        .catch(() => live && setFound([]));
    }, 200);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [query, enabled]);
  return found;
}

type NameValue = { name: string; patientId: string | null };

/**
 * The name, with patients from the list suggested as it is typed. Keyboard:
 * arrows move through the suggestions, Enter picks one, Escape closes them.
 */
function NameField({
  value,
  onChange,
  onPick,
  autoFocus,
}: {
  value: NameValue;
  onChange: (v: NameValue) => void;
  onPick: (patient: Patient) => void;
  /** A new booking: the sheet opens with the name focused. */
  autoFocus: boolean;
}) {
  const { t } = useTranslation();
  const [picking, setPicking] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const suggestions = useSuggestions(value.name, picking && !value.patientId);
  const combo = useCombobox({
    id: 'booking-suggestions',
    items: suggestions,
    onPick: (patient) => {
      onPick(patient);
      setPicking(false);
    },
    onClose: () => setPicking(false),
    onOpen: () => setPicking(true),
  });
  return (
    <div className="field autocomplete" ref={box}>
      <label htmlFor="booking-name">{t('calendar.name')}</label>
      <input
        id="booking-name"
        className="input"
        required
        data-autofocus={autoFocus || undefined}
        autoComplete="off"
        {...combo.input}
        value={value.name}
        onFocus={() => setPicking(true)}
        onBlur={(e) => {
          // Close the suggestions when focus leaves the field and its list.
          if (!box.current?.contains(e.relatedTarget as Node | null)) setPicking(false);
        }}
        onChange={(e) => {
          // Typing again unlinks the patient: the name is free text then.
          onChange({ name: e.target.value, patientId: null });
          setPicking(true);
          combo.reset();
        }}
      />
      {suggestions.length > 0 && (
        <div
          {...combo.list}
          role="listbox"
          className="autocomplete__list"
          aria-label={t('calendar.suggestions')}
        >
          {suggestions.map((s, i) => (
            <div
              key={s.id}
              {...combo.option(i, s)}
              className={`autocomplete__option ${i === combo.active ? 'is-active' : ''}`}
            >
              <strong>
                {s.lastName} {s.firstName}
              </strong>
              <span className="small muted">
                {[s.birthDate?.slice(0, 4), s.phone].filter(Boolean).join(' · ')}
              </span>
            </div>
          ))}
        </div>
      )}
      <span className="small muted">
        {value.patientId ? t('calendar.linked') : t('calendar.freeText')}
      </span>
    </div>
  );
}

/** Open the linked chart, or cancel the booking. */
function BookingActions({
  booking,
  onCancel,
}: {
  booking: Booking;
  onCancel?: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const patient = booking.patient;
  return (
    <div className="row">
      {patient && (
        <button
          type="button"
          className="btn btn--sm"
          onClick={() => navigate({ name: 'patient', id: patient.id })}
        >
          <IconExternalLink size={16} /> {t('calendar.openChart')}
        </button>
      )}
      <span className="grow" />
      {onCancel && (
        <button
          type="button"
          className="btn btn--sm"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            // The panel shows an error toast and puts the booking back on failure.
            await onCancel().catch(() => undefined);
            setBusy(false);
          }}
        >
          <IconTrash size={16} /> {t('calendar.cancelBooking')}
        </button>
      )}
    </div>
  );
}

/**
 * New or existing booking: the name (suggested from the patient list, or free
 * text that does not create a patient), phone, time, length and doctor.
 */
export function BookingSheet(p: Props) {
  const { t } = useTranslation();
  const b = p.booking;
  const [who, setWho] = useState<NameValue>({
    name: b?.name ?? '',
    patientId: b?.patient?.id ?? null,
  });
  const [phone, setPhone] = useState(b?.phone ?? '');
  const [minute, setMinute] = useState(minutesOfDay(p.start));
  const [duration, setDuration] = useState(b?.durationMin ?? SLOT_MIN);
  const [dentistId, setDentistId] = useState<string | null>(b?.dentist?.id ?? p.defaultDoctorId);
  const [busy, setBusy] = useState(false);
  const times = Array.from({ length: (24 * 60) / SLOT_MIN }, (_, i) => i * SLOT_MIN);
  const name = who.name.trim();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name) return;
    setBusy(true);
    try {
      await p.onSave({
        startsAt: atMinute(p.start, minute).toISOString(),
        durationMin: duration,
        name,
        phone: phone.trim() || null,
        patientId: who.patientId,
        dentistId,
      });
      p.onClose();
    } catch {
      // The calendar panel shows the error; the sheet stays open to try again.
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet
      title={
        b
          ? t('calendar.editTitle')
          : t('calendar.newTitle', { date: formatDate(p.start.toISOString()) })
      }
      onClose={p.onClose}
    >
      <form className="stack" onSubmit={submit}>
        <NameField
          autoFocus={!b}
          value={who}
          onChange={setWho}
          onPick={(patient) => {
            setWho({ name: `${patient.lastName} ${patient.firstName}`, patientId: patient.id });
            if (!phone && patient.phone) setPhone(patient.phone);
          }}
        />

        <div className="field">
          <label htmlFor="booking-phone">{t('calendar.phone')}</label>
          <input
            id="booking-phone"
            className="input"
            type="tel"
            autoComplete="off"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </div>

        <div className="form-grid">
          <div className="field">
            <label htmlFor="booking-time">{t('calendar.time')}</label>
            <Dropdown
              id="booking-time"
              value={minute}
              options={times.map((m) => ({ value: m, label: clock(m) }))}
              onChange={setMinute}
            />
          </div>
          <div className="field">
            <label htmlFor="booking-duration">{t('calendar.duration')}</label>
            <Dropdown
              id="booking-duration"
              value={duration}
              options={DURATIONS.map((d) => ({
                value: d,
                label: t('calendar.minutes', { count: d }),
              }))}
              onChange={setDuration}
            />
          </div>
        </div>

        {p.doctors.length > 0 && (
          <div className="field">
            <label htmlFor="booking-doctor">{t('calendar.doctor')}</label>
            <Dropdown
              id="booking-doctor"
              value={dentistId ?? ''}
              options={[
                { value: '', label: t('calendar.anyDoctor') },
                ...p.doctors.map((d) => ({ value: d.id, label: d.name })),
              ]}
              onChange={(v) => setDentistId(v || null)}
            />
          </div>
        )}

        <button type="submit" className="btn btn--dark btn--block" disabled={busy || !name}>
          {t(b ? 'common.save' : 'calendar.book')}
        </button>

        {b && (
          <BookingActions
            booking={b}
            onCancel={
              p.onCancelBooking
                ? async () => {
                    await p.onCancelBooking?.();
                    p.onClose();
                  }
                : undefined
            }
          />
        )}
      </form>
    </Sheet>
  );
}
